/*
  Forms for documents and links: a kind of document (with its kind of deliverable A, B or C), a document link
  ("is shown on", "is described by", "is specified by") and a link between items (7.10).
*/

import { Button, IconButton } from "../../../ui";
import { ElementPicker, NONE } from "../components/ElementPicker";
import { Check, PairList, RadioGroup, Select, StringList, TextInput, pairsToRecord, recordToPairs, type Pair } from "../components/fields";
import { isWholeNumber, snake, splitList } from "../lib/text";
import { DELIVERABLE_KIND_WORDS, LINK_TYPE_WORDS } from "../lib/words";
import type { DeliverableKind, LinkType } from "../../../api/types";
import { bool, defineSpec, rec, str, type Errors, type FieldsProps } from "./types";

// ------------------------------------------------------------------------------------------------ document kind

type SlotRow = { code: string; linkType: LinkType };
type GroupRow = { groupCode: string; fields: string };

type DocumentKindValues = {
  mainParentId: string;
  isDrawing: boolean;
  deliverableKind: DeliverableKind | "";
  onTargetList: boolean;
  linkSlots: SlotRow[];
  operatingCases: string[];
  sheetGroups: GroupRow[];
  printedLabels: Pair[];
  calculatedFrom: Pair[];
  fromProjectRecord: Pair[];
};

const LINK_TYPES: LinkType[] = ["is_shown_on", "is_described_by", "is_specified_by"];

function SlotRows({ rows, onChange, errors }: { rows: SlotRow[]; onChange: (rows: SlotRow[]) => void; errors: Errors }) {
  return (
    <fieldset className="field field--group">
      <legend className="field__label">Narrower links</legend>
      <p className="field__hint muted">A name for each link an item or component can have to this kind of document, and what the link says.</p>
      {errors.linkSlots && (
        <p role="alert" className="field__error">
          {errors.linkSlots}
        </p>
      )}
      <ul className="row-list">
        {rows.map((row, index) => (
          <li key={index} className="row">
            <input
              className="field__input"
              aria-label={`Narrower link ${index + 1}: name`}
              placeholder="Name of the link"
              value={row.code}
              onChange={(event) => onChange(rows.map((r, i) => (i === index ? { ...r, code: event.target.value } : r)))}
            />
            <select
              className="field__input"
              aria-label={`Narrower link ${index + 1}: what the link says`}
              value={row.linkType}
              onChange={(event) => onChange(rows.map((r, i) => (i === index ? { ...r, linkType: event.target.value as LinkType } : r)))}
            >
              {LINK_TYPES.map((type) => (
                <option key={type} value={type}>
                  {LINK_TYPE_WORDS[type]}
                </option>
              ))}
            </select>
            <IconButton label={`Take out narrower link ${index + 1}`} onClick={() => onChange(rows.filter((_r, i) => i !== index))}>
              ×
            </IconButton>
          </li>
        ))}
      </ul>
      <div>
        <Button variant="quiet" onClick={() => onChange([...rows, { code: "", linkType: "is_shown_on" }])}>
          Add a narrower link
        </Button>
      </div>
    </fieldset>
  );
}

function GroupRows({ rows, onChange }: { rows: GroupRow[]; onChange: (rows: GroupRow[]) => void }) {
  return (
    <fieldset className="field field--group">
      <legend className="field__label">Groups on the sheet, in order</legend>
      <p className="field__hint muted">For each block the sheet prints: a name for the group and the fields in it, separated by commas.</p>
      <ul className="row-list">
        {rows.map((row, index) => (
          <li key={index} className="row">
            <input
              className="field__input"
              aria-label={`Sheet group ${index + 1}: name`}
              placeholder="Name of the group"
              value={row.groupCode}
              onChange={(event) => onChange(rows.map((r, i) => (i === index ? { ...r, groupCode: event.target.value } : r)))}
            />
            <input
              className="field__input"
              aria-label={`Sheet group ${index + 1}: fields`}
              placeholder="Fields, separated by commas"
              value={row.fields}
              onChange={(event) => onChange(rows.map((r, i) => (i === index ? { ...r, fields: event.target.value } : r)))}
            />
            <IconButton label={`Take out sheet group ${index + 1}`} onClick={() => onChange(rows.filter((_r, i) => i !== index))}>
              ×
            </IconButton>
          </li>
        ))}
      </ul>
      <div>
        <Button variant="quiet" onClick={() => onChange([...rows, { groupCode: "", fields: "" }])}>
          Add a group
        </Button>
      </div>
    </fieldset>
  );
}

function DocumentKindFields({ values, set, errors, elementId }: FieldsProps<DocumentKindValues>) {
  return (
    <>
      <RadioGroup
        legend="Kind of deliverable"
        required
        hint="A: generated fully from data. B: data plus a drawing tool. C: geometric."
        value={values.deliverableKind}
        error={errors.deliverableKind}
        options={(["A", "B", "C"] as const).map((kind) => ({ value: kind, label: DELIVERABLE_KIND_WORDS[kind] }))}
        onChange={(deliverableKind) => set({ deliverableKind: deliverableKind as DeliverableKind })}
      />
      <Check label="It is a drawing" hint="Items and components can be shown on a drawing." checked={values.isDrawing} onChange={(isDrawing) => set({ isDrawing })} />
      <Check
        label="It is on the target list"
        hint="The deliverables the project must produce. The coverage check measures against this list."
        checked={values.onTargetList}
        onChange={(onTargetList) => set({ onTargetList })}
      />
      <ElementPicker
        label="Kind of (main parent)"
        hint="Another kind of document this one is a kind of."
        kinds={["document_kind"]}
        value={values.mainParentId}
        placeholder="None: it stands alone"
        exclude={elementId ? [elementId] : undefined}
        onChange={(mainParentId) => set({ mainParentId })}
      />
      <SlotRows rows={values.linkSlots} errors={errors} onChange={(linkSlots) => set({ linkSlots })} />
      <StringList
        label="Operating cases it prints"
        hint="Each project names its cases; list the ones this kind of document prints."
        values={values.operatingCases}
        addLabel="Add an operating case"
        onChange={(operatingCases) => set({ operatingCases })}
      />
      <GroupRows rows={values.sheetGroups} onChange={(sheetGroups) => set({ sheetGroups })} />
      <PairList
        label="Printed labels"
        hint="Where the document prints a word that differs from our name."
        rows={values.printedLabels}
        keyLabel="Our name"
        valueLabel="Printed word"
        addLabel="Add a printed label"
        onChange={(printedLabels) => set({ printedLabels })}
      />
      <PairList
        label="Lines that are calculated"
        hint="A printed line that is a sum or a result, and the lines it is calculated from."
        rows={values.calculatedFrom}
        keyLabel="Printed line"
        valueLabel="Calculated from"
        addLabel="Add a calculated line"
        onChange={(calculatedFrom) => set({ calculatedFrom })}
      />
      <PairList
        label="Values from the project record"
        hint="Title block values that come from the project record."
        rows={values.fromProjectRecord}
        keyLabel="Printed line"
        valueLabel="Where it comes from"
        addLabel="Add a value from the project record"
        onChange={(fromProjectRecord) => set({ fromProjectRecord })}
      />
    </>
  );
}

export const documentKindSpec = defineSpec<DocumentKindValues>({
  kind: "document_kind",
  title: "Document kind",
  blurb: "A kind of document with its own data fields, such as a sheet, a datasheet or a specification.",
  nameLabel: "Name of the document kind",
  definitionHint: "What this kind of document is for.",
  empty: (draft) => ({
    mainParentId: draft?.main_parent_id ?? "",
    isDrawing: false,
    deliverableKind: draft?.deliverable_kind ?? "",
    onTargetList: false,
    linkSlots: [],
    operatingCases: [],
    sheetGroups: [],
    printedLabels: [],
    calculatedFrom: [],
    fromProjectRecord: [],
  }),
  fromElement: (row) => {
    const c = row.content;
    const letter = str(c.deliverable_kind);
    return {
      mainParentId: str(c.main_parent_id),
      isDrawing: bool(c.is_drawing),
      deliverableKind: letter === "A" || letter === "B" || letter === "C" ? letter : "",
      onTargetList: bool(c.on_target_list),
      linkSlots: Array.isArray(c.link_slots)
        ? c.link_slots.map((slot) => {
            const s = rec(slot);
            const type = str(s.link_type) as LinkType;
            return { code: str(s.code), linkType: LINK_TYPES.includes(type) ? type : "is_shown_on" };
          })
        : [],
      operatingCases: Array.isArray(c.operating_cases) ? c.operating_cases.filter((x): x is string => typeof x === "string") : [],
      sheetGroups: Array.isArray(c.sheet_groups)
        ? c.sheet_groups.map((group) => {
            const g = rec(group);
            return { groupCode: str(g.group_code), fields: Array.isArray(g.fields) ? g.fields.filter((x): x is string => typeof x === "string").join(", ") : "" };
          })
        : [],
      printedLabels: recordToPairs(c.printed_labels),
      calculatedFrom: recordToPairs(c.calculated_from),
      fromProjectRecord: recordToPairs(c.from_project_record),
    };
  },
  validate: (v) => {
    const errors: Errors = {};
    if (!v.deliverableKind) errors.deliverableKind = "Say which kind of deliverable it is: A, B or C.";
    const codes = v.linkSlots.map((slot) => slot.code.trim());
    if (codes.some((code) => code === "")) errors.linkSlots = "Give every narrower link a name, or take the row out.";
    else if (new Set(codes.map((code) => snake(code))).size !== codes.length) errors.linkSlots = "Two narrower links share a name. Give each its own.";
    return errors;
  },
  toContent: (v, base) => ({
    ...base,
    main_parent_id: v.mainParentId && v.mainParentId !== NONE ? v.mainParentId : null,
    is_drawing: v.isDrawing,
    deliverable_kind: v.deliverableKind || null,
    on_target_list: v.onTargetList,
    link_slots: v.linkSlots.map((slot) => ({ code: slot.code.trim(), link_type: slot.linkType })),
    operating_cases: v.operatingCases.map((c) => c.trim()).filter(Boolean),
    sheet_groups: v.sheetGroups.filter((g) => g.groupCode.trim()).map((g) => ({ group_code: g.groupCode.trim(), fields: splitList(g.fields) })),
    printed_labels: pairsToRecord(v.printedLabels),
    calculated_from: pairsToRecord(v.calculatedFrom),
    from_project_record: pairsToRecord(v.fromProjectRecord),
    linkml_extra: base?.linkml_extra ?? {},
  }),
  Fields: DocumentKindFields,
});

// ------------------------------------------------------------------------------------------------ document link

type DocumentLinkValues = {
  ownerId: string;
  ownerName: string;
  documentKindId: string;
  documentKindName: string;
  linkSlotCode: string;
  linkType: LinkType;
};

export const documentLinkSpec = defineSpec<DocumentLinkValues>({
  kind: "document_link",
  title: "Document link",
  blurb: "Says that an item or a component kind is shown on, described by or specified by a kind of document.",
  nameLabel: "Name of the document link",
  nameHint: "Leave empty to let the app write it from your answers.",
  definitionHint: "Leave empty to let the app write it from your answers.",
  autoText: (v) => {
    if (!v.ownerName || !v.documentKindName) return null;
    const verb = LINK_TYPE_WORDS[v.linkType];
    return { name: `${v.ownerName} ${verb} ${v.documentKindName}`, definition: `${v.ownerName} ${verb} ${v.documentKindName}.` };
  },
  empty: () => ({ ownerId: "", ownerName: "", documentKindId: "", documentKindName: "", linkSlotCode: "", linkType: "is_shown_on" }),
  fromElement: (row) => {
    const c = row.content;
    const type = str(c.link_type) as LinkType;
    return {
      ownerId: str(c.owner_id),
      ownerName: "",
      documentKindId: str(c.document_kind_id),
      documentKindName: "",
      linkSlotCode: str(c.link_slot_code),
      linkType: LINK_TYPES.includes(type) ? type : "is_shown_on",
    };
  },
  validate: (v) => {
    const errors: Errors = {};
    if (!v.ownerId) errors.ownerId = "Choose the item or component kind the link starts at.";
    if (!v.documentKindId) errors.documentKindId = "Choose the kind of document.";
    if (!v.linkSlotCode.trim()) errors.linkSlotCode = "Write the link name.";
    return errors;
  },
  toContent: (v, base) => ({
    ...base,
    owner_id: v.ownerId,
    link_slot_code: v.linkSlotCode.trim(),
    document_kind_id: v.documentKindId,
    link_type: v.linkType,
  }),
  Fields: ({ values, set, errors }) => (
    <>
      <ElementPicker
        label="Starts at"
        required
        kinds={["item", "component_kind"]}
        value={values.ownerId}
        error={errors.ownerId}
        onChange={(ownerId, summary) => set({ ownerId, ownerName: summary?.name ?? "" })}
      />
      <Select
        label="What the link says"
        required
        value={values.linkType}
        options={LINK_TYPES.map((type) => ({ value: type, label: LINK_TYPE_WORDS[type] }))}
        onChange={(linkType) => set({ linkType: linkType as LinkType })}
      />
      <ElementPicker
        label="Kind of document"
        required
        kinds={["document_kind"]}
        value={values.documentKindId}
        error={errors.documentKindId}
        onChange={(documentKindId, summary) => set({ documentKindId, documentKindName: summary?.name ?? "" })}
      />
      <TextInput
        label="Link name"
        required
        hint="The name this link has on the item or component kind it starts at."
        value={values.linkSlotCode}
        error={errors.linkSlotCode}
        onChange={(linkSlotCode) => set({ linkSlotCode })}
      />
    </>
  ),
});

// ------------------------------------------------------------------------------------------------ link

type LinkValues = {
  ownerId: string;
  shared: boolean;
  reverseName: string;
  rangeKind: "item" | "capability";
  rangeId: string;
  maxCount: string;
  ordered: boolean;
};

export const linkSpec = defineSpec<LinkValues>({
  kind: "link",
  title: "Link",
  blurb: "A reference from one item to another with no physical connection.",
  nameLabel: "Name of the link",
  nameHint: "How the starting item reads it.",
  definitionHint: "What the link says.",
  empty: () => ({ ownerId: "", shared: false, reverseName: "", rangeKind: "item", rangeId: "", maxCount: "", ordered: false }),
  fromElement: (row) => {
    const c = row.content;
    return {
      ownerId: str(c.owner_id),
      shared: bool(c.shared),
      reverseName: str(c.reverse_name),
      rangeKind: c.range_kind === "capability" ? "capability" : "item",
      rangeId: str(c.range_id),
      maxCount: c.max_count == null ? "" : String(c.max_count),
      ordered: bool(c.ordered),
    };
  },
  validate: (v) => {
    const errors: Errors = {};
    if (!v.shared && !v.ownerId) errors.ownerId = "Choose where the link starts, or make it a shared link.";
    if (!v.reverseName.trim()) errors.reverseName = "Name the link as the far end reads it.";
    if (!v.rangeId) errors.rangeId = "Choose what the link points to.";
    if (v.maxCount.trim() && !isWholeNumber(v.maxCount)) errors.maxCount = "Write a whole number of 1 or more, or leave it empty for any number.";
    return errors;
  },
  toContent: (v, base) => ({
    ...base,
    owner_id: v.shared ? null : v.ownerId,
    reverse_name: v.reverseName.trim(),
    range_kind: v.rangeKind,
    range_id: v.rangeId,
    max_count: v.maxCount.trim() ? Number(v.maxCount) : null,
    ordered: v.ordered,
    limit_origin: base?.limit_origin === "dexpi" ? "dexpi" : "our_team",
    shared: v.shared,
    linkml_extra: base?.linkml_extra ?? {},
  }),
  Fields: ({ values, set, errors }) => (
    <>
      <Check label="A shared link: several items use it for one meaning" checked={values.shared} onChange={(shared) => set({ shared, ownerId: shared ? "" : values.ownerId })} />
      {!values.shared && (
        <ElementPicker label="Starts at" required kinds={["item"]} value={values.ownerId} error={errors.ownerId} onChange={(ownerId) => set({ ownerId })} />
      )}
      <TextInput label="Name of the link at the far end" required value={values.reverseName} error={errors.reverseName} onChange={(reverseName) => set({ reverseName })} />
      <Select
        label="Points to"
        required
        value={values.rangeKind}
        options={[
          { value: "item", label: "An item" },
          { value: "capability", label: "Anything with a capability" },
        ]}
        onChange={(rangeKind) => set({ rangeKind: rangeKind as "item" | "capability", rangeId: "" })}
      />
      <ElementPicker
        label={values.rangeKind === "item" ? "Which item" : "Which capability"}
        required
        kinds={[values.rangeKind]}
        value={values.rangeId}
        error={errors.rangeId}
        onChange={(rangeId) => set({ rangeId })}
      />
      <TextInput label="How many" hint="Leave empty for any number. Write 1 for a single one." value={values.maxCount} error={errors.maxCount} onChange={(maxCount) => set({ maxCount })} />
      <Check label="They are kept in order" checked={values.ordered} onChange={(ordered) => set({ ordered })} />
    </>
  ),
});

